import React, { useState } from 'react';
import { CartSnapshot } from '../../hooks/useAgentConversation';
import { displayPartNumber } from '../../utils/displayPartNumber';

interface CartPanelProps {
  cart: CartSnapshot | null;
  disabled: boolean;
  onSendMessage: (message: string) => void;
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const availabilityLabel = (status: string | undefined): { label: string; tone: 'in-stock' | 'transfer' | 'vendor' } => {
  switch (status) {
    case 'in_stock':
      return { label: 'In stock', tone: 'in-stock' };
    case 'transfer_needed':
      return { label: 'Transfer', tone: 'transfer' };
    case 'vendor_order_needed':
      return { label: 'Vendor order', tone: 'vendor' };
    default:
      return { label: status || '—', tone: 'vendor' };
  }
};

const CartPanel: React.FC<CartPanelProps> = ({ cart, disabled, onSendMessage }) => {
  const [expanded, setExpanded] = useState(false);

  const items = cart?.items ?? [];
  const itemCount = items.reduce((sum, item) => sum + (item.quantity || 0), 0);

  if (!cart || items.length === 0) {
    return null;
  }

  const handleRemove = (partNumber: string) => {
    if (disabled) return;
    onSendMessage(`Please remove ${partNumber} from my cart.`);
  };

  const handleSubmit = () => {
    if (disabled) return;
    onSendMessage('Please submit my parts order.');
  };

  const handleClear = () => {
    if (disabled) return;
    onSendMessage('Please clear my cart.');
  };

  return (
    <div className={`cart-panel${expanded ? ' cart-panel--expanded' : ''}`}>
      <button
        type="button"
        className="cart-panel__summary"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <span className="cart-panel__summary-left">
          <span className="cart-panel__label">Cart</span>
          <span className="cart-panel__count">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
          {cart.location_name && (
            <span className="cart-panel__location">at {cart.location_name}</span>
          )}
        </span>
        <span className="cart-panel__summary-right">
          {cart.total != null && <span className="cart-panel__total">{formatCurrency(cart.total)}</span>}
          <svg
            className="cart-panel__chevron"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {expanded && (
        <div className="cart-panel__body">
          <ul className="cart-panel__items">
            {items.map((item) => {
              const avail = availabilityLabel(item.availability_status);
              const lineTotal = item.unit_price != null
                ? item.unit_price * item.quantity
                : null;
              return (
                <li key={item.part_number} className="cart-panel__item">
                  <div className="cart-panel__item-main">
                    <div className="cart-panel__item-part">{displayPartNumber(item.part_number)}</div>
                    {item.description && (
                      <div className="cart-panel__item-desc">{item.description}</div>
                    )}
                    <div className="cart-panel__item-meta">
                      <span className="cart-panel__item-qty">Qty {item.quantity}</span>
                      {item.unit_price != null && (
                        <span className="cart-panel__item-price">
                          {formatCurrency(item.unit_price)} ea
                        </span>
                      )}
                      <span className={`cart-panel__availability cart-panel__availability--${avail.tone}`}>
                        {avail.label}
                      </span>
                    </div>
                  </div>
                  <div className="cart-panel__item-right">
                    {lineTotal != null && (
                      <div className="cart-panel__item-total">{formatCurrency(lineTotal)}</div>
                    )}
                    <button
                      type="button"
                      className="cart-panel__remove"
                      onClick={() => handleRemove(item.part_number)}
                      disabled={disabled}
                      aria-label={`Remove ${item.part_number}`}
                      title={`Remove ${item.part_number}`}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="cart-panel__actions">
            <button
              type="button"
              className="cart-panel__btn cart-panel__btn--ghost"
              onClick={handleClear}
              disabled={disabled}
            >
              Clear
            </button>
            <button
              type="button"
              className="cart-panel__btn cart-panel__btn--primary"
              onClick={handleSubmit}
              disabled={disabled}
            >
              Submit order
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPanel;
